import React from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { StatusBadge } from '../../../components/ui';

/** Theme-aware Hub tokens (work in light + dark via semantic colors). */
export const hub = {
  muted: 'text.secondary',
  text: 'text.primary',
  cardBg: 'bg.surface',
  cardBorder: 'border.subtle',
  softBg: 'bg.input',
  pageBg: 'bg.canvas',
  ghostBorder: 'border.subtle',
  ghostHover: 'bg.input',
  thumbBg: 'bg.input',
  docEmptyBg: 'bg.input',
};

export function HubPageHeader({ title, subtitle, actions }) {
  return (
    <Flex
      align={{ base: 'stretch', md: 'flex-start' }}
      justify="space-between"
      gap={3}
      mb={3}
      flexWrap="wrap"
    >
      <Box minW={0} flex="1">
        <Heading size="md" color="text.primary" letterSpacing="-0.02em" mb={subtitle ? 0.5 : 0}>
          {title}
        </Heading>
        {subtitle ? (
          <Text color={hub.muted} fontSize="13px" maxW="720px" lineHeight="1.35">
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {actions ? (
        <Flex gap={2} flexWrap="wrap" flexShrink={0}>
          {actions}
        </Flex>
      ) : null}
    </Flex>
  );
}

export function HubCard({ label, value, hint, onClick, icon: Icon }) {
  return (
    <Box
      as="button"
      textAlign="left"
      w="100%"
      h="100%"
      bg={hub.cardBg}
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="12px"
      px={3.5}
      py={3}
      cursor="pointer"
      transition="border-color 0.15s, transform 0.15s"
      _hover={{ borderColor: 'utkarsh.amber', transform: 'translateY(-1px)' }}
      onClick={onClick}
    >
      <Flex align="center" gap={3} w="100%">
        {Icon ? (
          <Flex
            align="center"
            justify="center"
            w="44px"
            h="44px"
            borderRadius="10px"
            bg="bg.input"
            color="utkarsh.amber"
            flexShrink={0}
          >
            <Icon size={22} />
          </Flex>
        ) : null}
        <Box minW={0} flex="1">
          <Text
            fontSize="12px"
            fontWeight="700"
            letterSpacing="0.04em"
            textTransform="uppercase"
            color={hub.muted}
            noOfLines={1}
            mb={0.5}
          >
            {label}
          </Text>
          <Text
            fontSize="32px"
            fontWeight="800"
            color="text.primary"
            letterSpacing="-0.04em"
            lineHeight="1"
            mb={hint ? 1 : 0}
          >
            {value}
          </Text>
          {hint ? (
            <Text fontSize="13px" color={hub.muted} noOfLines={1} lineHeight="1.3">
              {hint}
            </Text>
          ) : null}
        </Box>
      </Flex>
    </Box>
  );
}

/**
 * Rich summary card — icon + title + count as one aligned block.
 * metrics: [{ label, value }] (centered 3-col row)
 * alert: { label, value } | null
 */
export function HubMetricCard({
  label,
  total,
  totalLabel = 'Total',
  alert,
  metrics = [],
  onClick,
  icon: Icon,
}) {
  const alertValue = Number(alert?.value) || 0;
  const showAlert = Boolean(alert && alertValue > 0);
  // Only show metrics that belong to this card — keep to 4 so labels stay readable
  const slots = metrics.slice(0, 4);

  return (
    <Box
      as="button"
      textAlign="left"
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      gap={2.5}
      bg={hub.cardBg}
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="12px"
      px={3.5}
      py={3}
      cursor="pointer"
      transition="border-color 0.15s, transform 0.15s"
      _hover={{ borderColor: 'utkarsh.amber', transform: 'translateY(-1px)' }}
      onClick={onClick}
    >
      {/* Header: icon | title + total count */}
      <Flex align="center" gap={3} w="100%">
        {Icon ? (
          <Flex
            align="center"
            justify="center"
            w="44px"
            h="44px"
            borderRadius="10px"
            bg="bg.input"
            color="utkarsh.amber"
            flexShrink={0}
          >
            <Icon size={22} />
          </Flex>
        ) : null}
        <Box minW={0} flex="1">
          <Text
            fontSize="12px"
            fontWeight="700"
            letterSpacing="0.04em"
            textTransform="uppercase"
            color={hub.muted}
            noOfLines={1}
            mb={0.5}
          >
            {label}
          </Text>
          <Flex align="baseline" gap={2} flexWrap="nowrap">
            <Text
              fontSize="32px"
              fontWeight="800"
              color="text.primary"
              letterSpacing="-0.04em"
              lineHeight="1"
            >
              {total}
            </Text>
            <Text fontSize="13px" color={hub.muted} fontWeight="500" flexShrink={0}>
              {totalLabel}
            </Text>
          </Flex>
        </Box>
      </Flex>

      {showAlert ? (
        <Flex
          align="center"
          justify="center"
          px={2.5}
          py={1.5}
          borderRadius="6px"
          bg="bg.input"
          border="1px solid"
          borderColor="status.warning"
          w="100%"
        >
          <Text fontSize="13px" fontWeight="700" color="status.warning" noOfLines={1}>
            {alertValue} {alert.label}
          </Text>
        </Flex>
      ) : null}

      {slots.length ? (
        <SimpleGrid
          columns={slots.length}
          spacing={2}
          w="100%"
          pt={2}
          borderTop="1px solid"
          borderColor={hub.cardBorder}
        >
          {slots.map((m) => (
            <Box key={m.label} textAlign="center" minW={0}>
              <Text
                fontSize="12px"
                color={hub.muted}
                fontWeight="500"
                lineHeight="1.2"
                mb={0.5}
                noOfLines={1}
              >
                {m.label}
              </Text>
              <Text fontSize="18px" fontWeight="700" color="text.primary" lineHeight="1.15">
                {m.value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      ) : null}
    </Box>
  );
}

export function HubSectionLabel({ children }) {
  return (
    <Text
      fontSize="10px"
      fontWeight="700"
      letterSpacing="0.08em"
      textTransform="uppercase"
      color={hub.muted}
      mb={2}
    >
      {children}
    </Text>
  );
}

export function HubPanel({ title, children, actions }) {
  return (
    <Box
      bg={hub.cardBg}
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="16px"
      p={{ base: 4, md: 5 }}
      h="100%"
    >
      {(title || actions) && (
        <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
          {title ? (
            <Text fontWeight="600" fontSize="15px" color="text.primary">
              {title}
            </Text>
          ) : (
            <Box />
          )}
          {actions}
        </Flex>
      )}
      {children}
    </Box>
  );
}

export function HubRow({ label, children }) {
  return (
    <Flex
      justify="space-between"
      gap={4}
      py={2.5}
      borderBottom="1px solid"
      borderColor={hub.cardBorder}
      fontSize="14px"
      align="flex-start"
    >
      <Text color={hub.muted} flexShrink={0} minW="120px">
        {label}
      </Text>
      <Box textAlign="right" color={hub.text} wordBreak="break-word">
        {children ?? '—'}
      </Box>
    </Flex>
  );
}

export function HubAmberButton({ children, ...props }) {
  return (
    <Button
      size="sm"
      bg="utkarsh.amber"
      color="text.onAmber"
      fontWeight="600"
      _hover={{ bg: 'brand.600' }}
      _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
      {...props}
    >
      {children}
    </Button>
  );
}

export function HubGhostButton({ children, ...props }) {
  return (
    <Button
      size="sm"
      variant="outline"
      borderColor={hub.ghostBorder}
      color={hub.text}
      _hover={{ bg: hub.ghostHover }}
      _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
      {...props}
    >
      {children}
    </Button>
  );
}

export function HubFilterBar({ search, setSearch, searchPlaceholder, filters, onClear, extra }) {
  return (
    <Flex gap={2} flexWrap="wrap" mb={4} align="center">
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={searchPlaceholder}
        maxW={{ base: '100%', md: '300px' }}
        bg="bg.input"
        borderColor="border.subtle"
        color="text.primary"
        _placeholder={{ color: 'text.placeholder' }}
        _hover={{ borderColor: 'border.subtle' }}
        _focus={{ borderColor: 'utkarsh.amber', boxShadow: 'none' }}
        size="sm"
      />
      {(filters || []).map((f) => (
        <Select
          key={f.key}
          value={f.value}
          onChange={(e) => f.onChange(e.target.value)}
          maxW="180px"
          size="sm"
          bg="bg.input"
          borderColor="border.subtle"
          color="text.primary"
          _hover={{ borderColor: 'border.subtle' }}
          _focus={{ borderColor: 'utkarsh.amber', boxShadow: 'none' }}
        >
          {f.options.map((o) => (
            <option key={o.value} value={o.value} style={{ color: '#111' }}>
              {o.label}
            </option>
          ))}
        </Select>
      ))}
      {extra}
      {onClear ? (
        <HubGhostButton onClick={onClear}>Clear filters</HubGhostButton>
      ) : null}
    </Flex>
  );
}

export function HubTable({ columns, rows, onRowClick, loading }) {
  if (loading) {
    return (
      <Flex
        justify="center"
        align="center"
        py={16}
        border="1px solid"
        borderColor={hub.cardBorder}
        borderRadius="16px"
        bg={hub.cardBg}
      >
        <Spinner color="utkarsh.amber" />
      </Flex>
    );
  }

  return (
    <Box
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="16px"
      overflow="hidden"
      bg={hub.cardBg}
      w="100%"
    >
      <Box overflowX="auto">
        <Box as="table" w="100%" fontSize="14px" sx={{ borderCollapse: 'collapse' }}>
          <Box as="thead">
            <Box as="tr">
              {columns.map((c) => (
                <Box
                  as="th"
                  key={c.key}
                  textAlign="left"
                  px={4}
                  py={3}
                  fontSize="11px"
                  textTransform="uppercase"
                  letterSpacing="0.04em"
                  color={hub.muted}
                  borderBottom="1px solid"
                  borderColor={hub.cardBorder}
                  bg={hub.softBg}
                  whiteSpace="nowrap"
                >
                  {c.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box as="tbody">
            {rows.length === 0 ? (
              <Box as="tr">
                <Box
                  as="td"
                  colSpan={columns.length}
                  px={4}
                  py={10}
                  color={hub.muted}
                  textAlign="center"
                >
                  No rows match your filters.
                </Box>
              </Box>
            ) : (
              rows.map((row) => (
                <Box
                  as="tr"
                  key={row.id}
                  cursor={onRowClick ? 'pointer' : 'default'}
                  _hover={onRowClick ? { bg: hub.softBg } : undefined}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((c) => (
                    <Box
                      as="td"
                      key={c.key}
                      px={4}
                      py={3}
                      borderBottom="1px solid"
                      borderColor={hub.cardBorder}
                      verticalAlign="middle"
                      color="text.primary"
                    >
                      {c.render ? c.render(row) : row[c.key] ?? '—'}
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function HubThumb({ src, label }) {
  if (!src) {
    return (
      <Flex
        w="44px"
        h="44px"
        borderRadius="8px"
        bg={hub.thumbBg}
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Text fontSize="10px" color={hub.muted}>
          N/A
        </Text>
      </Flex>
    );
  }
  return (
    <Image
      src={src}
      alt={label || ''}
      w="44px"
      h="44px"
      objectFit="cover"
      borderRadius="8px"
      flexShrink={0}
      bg={hub.thumbBg}
    />
  );
}

export function kycBadgeProps(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'verified') return { status: 'verified' };
  if (s === 'rejected') return { status: 'error' };
  return { status: 'pending' };
}

export function HubKycBadge({ status, children }) {
  return (
    <StatusBadge {...kycBadgeProps(status)}>{children || status || 'Pending'}</StatusBadge>
  );
}

export function HubDocTile({ label, url, onOpen }) {
  const has = Boolean(url);
  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      border="1px solid"
      borderColor={hub.cardBorder}
      borderRadius="12px"
      overflow="hidden"
      bg={hub.docEmptyBg}
      cursor={has ? 'pointer' : 'default'}
      opacity={has ? 1 : 0.55}
      onClick={() => has && onOpen?.()}
      w="100%"
      _hover={has ? { borderColor: 'utkarsh.amber' } : undefined}
    >
      {has ? (
        <Image src={url} alt={label} w="100%" h="120px" objectFit="cover" />
      ) : (
        <Flex h="120px" align="center" justify="center">
          <Text fontSize="12px" color={hub.muted}>
            No file
          </Text>
        </Flex>
      )}
      <Text px={3} py={2} fontSize="12px" fontWeight="600" color={hub.text}>
        {label}
      </Text>
    </Box>
  );
}

export function HubDocRow({ label, url, onOpen }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={3}
      py={2.5}
      borderBottom="1px solid"
      borderColor={hub.cardBorder}
    >
      <Text fontSize="14px" color={hub.text}>
        {label}
      </Text>
      {url ? (
        <HubGhostButton onClick={onOpen}>View</HubGhostButton>
      ) : (
        <Text fontSize="12px" color={hub.muted}>
          Missing
        </Text>
      )}
    </Flex>
  );
}

export function HubLoading() {
  return (
    <Flex justify="center" align="center" minH="40vh">
      <VStack spacing={3}>
        <Spinner color="utkarsh.amber" size="lg" />
        <Text color={hub.muted} fontSize="14px">
          Loading…
        </Text>
      </VStack>
    </Flex>
  );
}

export function HubError({ message, onRetry }) {
  return (
    <HubPanel>
      <Text color="status.error" mb={3}>
        {message || 'Something went wrong'}
      </Text>
      {onRetry ? <HubGhostButton onClick={onRetry}>Retry</HubGhostButton> : null}
    </HubPanel>
  );
}
